/**
 * What a process wants sent with its trigger, and how the pieces are put together.
 *
 * Some processes take a single report and want it on the wire exactly as it was handed over.
 * Others need more than one file at a time - the survey processor wants an offerings export and
 * a submissions export together - and expect them as one JSON object keyed by part. Both are the
 * same job as far as this application is concerned: collect one piece of text per part, then
 * assemble the body. Describing that with a spec rather than a boolean is what lets a new process
 * with a different set of parts be added to the list without touching the form or the trigger.
 */

/** How the collected parts become the request body. */
export type ProcessBodyFormat =
  /** The single part's content, verbatim - nothing wrapped, nothing escaped. */
  | 'raw'
  /** A JSON object of `{ [part key]: part content }`, in the order the parts are declared. */
  | 'json';

/** One piece of content to collect for a process. */
export interface ProcessBodyPart {
  /**
   * Property this part fills in a `json` body. Also its identity in the form, so it has to be
   * unique within a process even when the format ignores it.
   */
  key: string;
  /** Field label in the form, and the part's accessible name. */
  label: string;
  /** Line under the field: what this part is and where it comes from. */
  helperText?: string;
  /**
   * What this part's file picker offers, in react-dropzone's shape - `{ 'text/csv': ['.csv'] }`.
   * Left unset by default: the service forwards the body verbatim, so nothing here knows which
   * format a given process wants, and a wrong restriction hides the file the sender actually has.
   */
  accept?: Record<string, string[]>;
}

export interface ProcessBodySpec {
  format: ProcessBodyFormat;
  /** Every part is required; a process that asked for two files is not run with one. */
  parts: ProcessBodyPart[];
}

/**
 * Assembles the body for `spec` from the content collected per part.
 *
 * The parts themselves are never parsed or trimmed - they belong to the automation system, not to
 * this application. A `json` body only adds the wrapper the receiving process asked for, and does
 * it from `spec.parts` rather than from the collected keys so the object always carries exactly
 * the declared properties, whatever the form happened to hold.
 *
 * The result is a string, and it travels as `AutomationApi.triggerProcess` sends every body, as
 * text/plain: fetchData serialises the data itself for a JSON content type, so declaring one here
 * would deliver this string quoted and escaped rather than the object the process is waiting for.
 */
export const buildProcessBody = (spec: ProcessBodySpec, collected: Record<string, string>): string => {
  if (spec.format === 'json') {
    const body: Record<string, string> = {};
    spec.parts.forEach(({ key }) => {
      body[key] = collected[key] ?? '';
    });
    return JSON.stringify(body);
  }
  const [only] = spec.parts;
  return (only && collected[only.key]) ?? '';
};

/** True once every part has content, which is what the trigger waits for. */
export const isProcessBodyComplete = (spec: ProcessBodySpec, collected: Record<string, string>): boolean =>
  spec.parts.every(({ key }) => (collected[key] ?? '').trim().length > 0);
